import React, {useEffect, useState} from "react";
import {Settings} from "../../../models/models";
import {AC_DEV_LEVEL, AC_PAGES, PAYMENT_TYPE, SUBSCRIPTION_ROOT_PAGES} from "../../../constants";
import {
    cancelPaymentIntent,
    cancelSetupIntent,
    createCustomerForAccount,
    createPaymentIntent,
    createSetupIntent, setACDoNotRenew
} from "../../../api/requests";
import useBeforeUnload from "../../../helpers/useBeforeUnload";
import AcSplashPage from "./pages/AcSplashPage";
import AcPlanDetailsPage from "./pages/AcPlanDetailsPage";
import {Elements} from "@stripe/react-stripe-js";
import {loadStripe} from "@stripe/stripe-js";
import AcCheckoutPage from "./pages/AcCheckoutPage";
import Loading from "../../common/Loading";
import GenericItem from "../../common/items/GenericItem";
import {getFormattedMessage} from "../../../helpers/helpers";
import ClickToCopyItem from "../../common/items/ClickToCopyItem";
import PlanHeaderNotes from "../../common/subscriptions/PlanHeaderNotes";
import ManagePage from "../common/ManagePage";


const AcContainer = (props: {
    data: Object,
    setData: Function,
    rootPage: number,
    settings: Settings,
    handleError: Function,
    setRootPage: Function,
    setServerResponse: Function,
    setShouldRefresh: Function,
    preferredACPaymentMethod: Object
}) => {
    const {
        data, setData, settings, handleError, setServerResponse,
        rootPage, setRootPage, setShouldRefresh, preferredACPaymentMethod
    } = props;

    if (!data || !settings || rootPage !== SUBSCRIPTION_ROOT_PAGES.AC) {
        return <></>;
    }

    const {
        price, devLevel, product, customerId, expirationDate, isClientOfReseller,
        resellerName, resellerEmail, resellerPhone
    } = data;

    const {role, publishableKey} = settings;
    const isBasic = devLevel === AC_DEV_LEVEL.BASIC;

    const [page, setPage] = useState(AC_PAGES.SPLASH);
    const [loading, setLoading] = useState(false);
    const [clientSecretKey, setClientSecretKey] = useState(""); // Unique checkout session key for customer.
    const [payNow, setPayNow] = useState(false);

    // A PaymentIntent is used to keep track of the payment flow and is created upon
    // React app load: https://stripe.com/docs/payments/payment-intents
    const [paymentIntentId, setPaymentIntentId] = useState("");

    useEffect(() => { // User selects a plan
        if (page === AC_PAGES.PLAN_DETAILS && !customerId) {
            createCustomer();
        } else if (page === AC_PAGES.CHECKOUT) {
            if (!publishableKey || !price || !product) {
                console.error('Failed to load checkout form.');
                handleError(i18n.payments.error.genericTitle, i18n.payments.error.failedToLoadCheckoutForm, setServerResponse);
                return;
            } else if (!customerId) {
                createCustomer(() => {
                    createIntent();
                });
                return;
            }

            createIntent();
        }
    }, [page]);

    const createCustomer = (callback) => {
        setLoading(true);
        createCustomerForAccount((result) => {
            setLoading(false);
            if (result.status !== 200) {
                console.error('Failed to create customer account');
                handleError(result, i18n.payments.error.genericServerError, setServerResponse);
                return;
            }

            setData({
                ...data,
                customerId: result.data.customerId,
            });

            if (callback) {
                callback();
            }
        });
    };

    const createIntent = () => {
        setLoading(true);
        setPaymentIntentId("");

        if (payNow) {
            createPaymentIntent(product.id, PAYMENT_TYPE.AC, (result) => {
                setLoading(false);
                if (result.status !== 200) {
                    console.error('Failed to create payment intent');
                    handleError(result, i18n.payments.error.genericServerError, setServerResponse);
                    return;
                }
                setPaymentIntentId(result.data.id);
                setClientSecretKey(result.data.clientSecret);
            });
        } else {
            createSetupIntent(product.id, PAYMENT_TYPE.AC, (result) => {
                setLoading(false);
                if (result.status !== 200) {
                    console.error('Failed to create setup intent');
                    handleError(result, i18n.payments.error.genericServerError, setServerResponse);
                    return;
                }
                setPaymentIntentId(result.data.id);
                setClientSecretKey(result.data.clientSecret);
            });
        }
    };

    // Clean Up //////////////////////////////////////////////////////////////
    const handlePreventDefault = (event) => {
        event.preventDefault(); // Cancel the event as stated by the standard.
        event.returnValue = ''; // Chrome requires returnValue to be set.
    }

    const handleLeavingPage = (event) => {
        // Cancel any payment attempts that have not yet been processed.
        if (page === AC_PAGES.CHECKOUT && paymentIntentId) {
            handlePreventDefault(event);

            if (paymentIntentId.includes('seti')) {
                cancelSetupIntent(paymentIntentId, (result) => {
                });
            } else {
                cancelPaymentIntent(paymentIntentId, (result) => {
                });
            }
        }
    };

    // Custom hook to handle applying the 'beforeunload' event listener smoothly.
    useBeforeUnload(handleLeavingPage);

    // UI ///////////////////////////////////////////////////////////////////////////
    let Body = <></>;
    if (loading) {
        Body = (<Loading show={true}/>);
    } else if (isBasic || isClientOfReseller) {
        Body = (
            <div data-testid={'managedByResellerNoteDiv'}>
                <GenericItem title={i18n.common.details}
                             details={getFormattedMessage(i18n.payments.ac.managedByResellerNote, resellerName, resellerName)}/>
                <ClickToCopyItem title={i18n.payments.resellerEmail} details={resellerEmail}/>
                <ClickToCopyItem title={i18n.payments.resellerPhone} details={resellerPhone}/>
            </div>
        );
    } else if (page === AC_PAGES.SPLASH) {
        Body = (<AcSplashPage appCenterData={data}
                              settings={settings}
                              setPage={setPage}
                              setRootPage={setRootPage}
                              setPayNow={setPayNow}/>);
    } else if (page === AC_PAGES.PLAN_DETAILS) {
        // Awaiting a price selection so that a PaymentIntent can be created and a
        // client secret can be issued from Stripe.
        Body = (<AcPlanDetailsPage product={product}
                                   acData={data}
                                   setAcData={setData}
                                   price={price}
                                   payNow={payNow}
                                   role={role}
                                   expirationDate={expirationDate}
                                   setPage={setPage}/>);
    } else if (page === AC_PAGES.CHECKOUT && clientSecretKey) {
        Body = (
            <Elements stripe={loadStripe(publishableKey)}
                      options={{clientSecret: clientSecretKey}}>
                <AcCheckoutPage appCenterData={data}
                                product={product}
                                price={price}
                                payNow={payNow}
                                role={role}
                                expirationDate={expirationDate}
                                paymentIntentId={paymentIntentId}
                                handleServerError={handleError}
                                setServerResponse={setServerResponse}
                                setPage={setPage}/>
            </Elements>
        );
    } else if (page === AC_PAGES.MANAGE) {
        Body = (
            <ManagePage
            data={data}
            pages={AC_PAGES}
            setPage={setPage}
            setPayNow={setPayNow}
            setLoading={setLoading}
            setServerResponse={setServerResponse}
            setShouldRefresh={setShouldRefresh}
            preferredPaymentMethod={preferredACPaymentMethod}
            paymentType={PAYMENT_TYPE.AC}
            hasSubscription={false}
            setDoNotRenew={setACDoNotRenew}/>);
    }

    return (
        <div className={`plan-container`} data-testid={'acContainer'}>
            <PlanHeaderNotes title={i18n.payments.ac.title}
                             subtitle={i18n.payments.ac.productNote}
                             testid={'acPlanHeaderNotesDiv'}
                             paymentType={PAYMENT_TYPE.AC}
                             data={data}/>

            {
                <GenericItem details={i18n.payments.ac.notRequiredForVRI}/>
            }

            { Body }

            {
                !isClientOfReseller && (
                    <div className={`flexSpaceBetween`}>
                        <div className={`flex margin-top-1`}>
                        </div>
                    </div>
                )
            }
        </div>
    );
}

export default AcContainer;